<template>
  <div class="xpage-content-page" id="xsearch-page">
    <Loader v-if="loading || !showFilters" />
    <Page v-else>
      <SearchPageBreadcrumbs :pageName="props.pageName"></SearchPageBreadcrumbs>
      <div class="xpage-inner section">
        <SearchPageLayoutButtons
          @changeLayout="searchPageStore.changeLayout"
          :settings="searchPageState"
        />
        <h2>{{ props.pageName }}</h2>
        <div
          name="xpage-searchbar-wrapper"
          class="search-page-searchbar-wrapper"
        >
          <SearchPageSearchBar
            :pageName="pageName"
            :searchBoxPlaceholderText="searchBoxPlaceholderText"
          />
          <SearchPageSearchBarDropdownResults :pageName="pageName" />
          <SearchPageMobileMenu />
        </div>
      </div>
      <div class="xpage-inner section">
        <div class="search-page-search-wrapper">
          <div
            name="xpage-searchfilters-wrapper"
            class="search-page-searchfilters-wrapper"
            v-if="showFilters"
          >
            <SearchPageFilters
              :filterDefinitions="searchPageStore.searchPageState.filters"
              :settings="searchPageStore.searchPageState"
            ></SearchPageFilters>
          </div>
        </div>
        <div class="xpage-searchresults-wrapper">
          <SearchPageSearchResults :pageName="props.pageName" />
        </div>
      </div>
    </Page>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch, ref, onBeforeMount, PropType, computed } from "vue";
import Page from "@/components/layout/Page.vue";
import Loader from "@/components/Loader.vue";
import SearchPageBreadcrumbs from "@/components/v2/search-page/SearchPageBreadcrumbs.vue";
import SearchPageLayoutButtons from "@/components/v2/search-page/SearchPageLayoutButtons.vue";
import SearchPageSearchBar from "@/components/v2/search-page/SearchPageSearchBar.vue";
import SearchPageFilters from "@/components/v2/search-page/SearchPageFilters.vue";
import SearchPageSearchResults from "@/components/v2/search-page/SearchPageSearchResults.vue";
import SearchPageMobileMenu from "@/components/v2/search-page/SearchPageMobileMenu.vue";
import SearchPageSearchBarDropdownResults from "@/components/v2/search-page/SearchPageSearchBarDropdownResults.vue";
import { useSearchPageStore } from "@/store/store/searchPageStore";
import { useRoute, useRouter } from "vue-router";
import * as jsonpatch from "fast-json-patch";

const route = useRoute();

const router = useRouter();

const props = defineProps({
  pageName: {
    type: String,
    default: "",
  },
  searchBoxPlaceholderText: {
    type: String,
    default: "",
  },
});

const searchPageStore = useSearchPageStore();

const loading = ref<boolean>(true);

const filters = computed(() => searchPageStore.searchPageState.filters);

const searchPageState = computed(() => searchPageStore.searchPageState);

const showFilters = computed<boolean>(() => filters.value?.length > 0);

async function checkQueryChanges(oldValue, newValue) {
  interface Patch {
    op: string;
    path: string;
    value?: any;
  }

  const ignoreKeys = ["Layout", "CollapsedFilters", "ExpandedFilters"];

  const keysThatTriggerSearch = [
    "pageNumber",
    "PageNumber",
    "pageSize",
    "PageSize", 
    "resultSortKey",
    "resultSortOrder",
  ];

  const difference = jsonpatch.compare(oldValue, newValue);

  if (difference.length === 0) {
    return;
  }

  const changedKeys = difference.map(
    (difference: Patch) => difference.path.split("/")[1]
  );

  const shouldSearch = changedKeys.some(
    (key) => !ignoreKeys.includes(key)
  );

  if (!shouldSearch) {
    return;
  }
  
  const isPageChangeOnly = changedKeys.every((key) => 
    keysThatTriggerSearch.includes(key)
  );

  const currentPage = newValue.pageNumber ?? newValue.PageNumber ?? 1;

  if (!isPageChangeOnly && currentPage !== 1) {
    searchPageStore.setPaginationPageNumber(1);

    await router.replace({
      query: {
        ...newValue,
        pageNumber: 1,
      },
    });

    return;
  }

  await searchPageStore.getParametersFromUrl(props.pageName);
  await searchPageStore.searchEntities();
}

watch(
  () => route.query,
  async (currentValue, oldValue) => {
    await checkQueryChanges(oldValue, currentValue);
  }
);

watch(
  () => props.pageName,
  async (currentValue) => {
    searchPageStore.searchPageState.entity = currentValue;
    searchPageStore.searchPageState.pageName = currentValue;
  }
);

watch(
  () => route.params,
  async () => {
    await searchPageStore.getParametersFromUrl(props.pageName);
  }
);

watch(
  () => filters.value.length,
  async (currentValue, oldValue) => {
    if (currentValue > 0) {
      await searchPageStore.getParametersFromUrl(props.pageName);
    }
  }
);

onMounted(async () => {
  searchPageStore.searchPageState.pageName = props.pageName;

  // #TODO if the localstore has any recently viewed records
  // if (localStorage.getItem(`cache.RecentlyViewedPages_${props.pageName}`)) {
  //   // retreive the data from localstorage
  //   applications.recentlyViewed = JSON.parse(
  //     localStorage.getItem(cache.RecentlyViewedPages_${props.pageName})
  //   ).filter((item) => item.page == props.pageName.toLowerCase());
  // }
  loading.value = false;
});
</script>

<style scoped src="@/assets/css/search-page/search-page.css"></style>
